import type { ThemeDefinition } from 'vuetify'

/**
 * Paleta de Blue Trails.
 *
 * Los tokens salen del sitio público (`website/assets/css/theme.min.css`, vars
 * `--si-*`) para que portal y sitio se lean como el mismo producto. Antes solo
 * `primary` era de marca y el resto era el default de Materio, cuyo neutro es
 * violáceo (`#2E263D`) — al lado del sitio, que usa un neutro azulado, el
 * portal se notaba "otra cosa".
 *
 * Equivalencias con las variables del sitio:
 *   --si-primary  #2f9fa0   --si-gray-100 #f3f6ff   --si-gray-600 #9397ad
 *   --si-success  #22c55e   --si-gray-200 #eff2fc   --si-gray-700 #565973
 *   --si-info     #4c82f7   --si-gray-300 #e2e5f1   --si-gray-800 #33354d
 *   --si-warning  #ffba08   --si-gray-400 #d4d7e5   --si-gray-900 #0b0f19
 *   --si-danger   #ef4444   --si-gray-500 #b4b7c9
 */
export const staticPrimaryColor = '#2F9FA0'
export const staticPrimaryDarkenColor = '#268B8C'

/**
 * El sitio público no tiene modo oscuro, así que la rampa oscura se derivó de
 * su tinta (`#0b0f19`) manteniendo el mismo sesgo azulado. No se copió la de
 * Materio, que es violeta y volvería a desalinear las dos superficies.
 */
export const themes: Record<string, ThemeDefinition> = {
  light: {
    dark: false,
    colors: {
      'primary': staticPrimaryColor,
      'on-primary': '#fff',
      'primary-darken-1': staticPrimaryDarkenColor,

      // `secondary` es el gris de acción de Vuetify (botones/chips atenuados).
      // Se toma el gris 700 del sitio en vez del `--si-secondary` (#eff2fc),
      // que allá es un fondo y acá dejaría el texto ilegible.
      'secondary': '#565973',
      'secondary-darken-1': '#494C63',
      'on-secondary': '#fff',

      'success': '#22C55E',
      'success-darken-1': '#1DAE53',
      'on-success': '#fff',
      'info': '#4C82F7',
      'info-darken-1': '#3B72E8',
      'on-info': '#fff',
      'warning': '#FFBA08',
      'warning-darken-1': '#E6A700',

      // Tinta oscura sobre el ámbar: con blanco (lo que traía la plantilla) el
      // contraste queda en ~1.9:1 y el botón se lee lavado.
      'on-warning': '#0B0F19',

      'error': '#EF4444',
      'error-darken-1': '#DC3A3A',
      'on-error': '#fff',
      'background': '#F3F6FF',
      'on-background': '#0B0F19',
      'surface': '#fff',
      'on-surface': '#0B0F19',
      'grey-50': '#F8FAFF',
      'grey-100': '#F3F6FF',
      'grey-200': '#EFF2FC',
      'grey-300': '#E2E5F1',
      'grey-400': '#D4D7E5',
      'grey-500': '#B4B7C9',
      'grey-600': '#9397AD',
      'grey-700': '#565973',
      'grey-800': '#33354D',
      'grey-900': '#0B0F19',
      'perfect-scrollbar-thumb': '#D4D7E5',
      'skin-bordered-background': '#fff',
      'skin-bordered-surface': '#fff',
      'expansion-panel-text-custom-bg': '#F3F6FF',
      'track-bg': '#EFF2FC',
      'chat-bg': '#F3F6FF',
    },

    variables: {
      'code-color': '#d400ff',
      'overlay-scrim-background': '#0B0F19',
      'tooltip-background': '#0B0F19',
      'overlay-scrim-opacity': 0.5,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'dragged-opacity': 0.1,
      'disabled-opacity': 0.4,

      // Los bordes se dibujan como esta color al 12%. Se usa el gris 800 y no
      // la tinta `#0B0F19` para que el borde salga frío como el `#e2e5f1` del
      // sitio, en vez de un gris neutro.
      'border-color': '#33354D',
      'border-opacity': 0.12,

      'table-header-color': '#EFF2FC',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,

      // 👉 shadows
      'shadow-key-umbra-color': '#0B0F19',
      'shadow-xs-opacity': '0.16',
      'shadow-sm-opacity': '0.18',
      'shadow-md-opacity': '0.20',
      'shadow-lg-opacity': '0.22',
      'shadow-xl-opacity': '0.24',
    },
  },

  dark: {
    dark: true,
    colors: {
      'primary': staticPrimaryColor,
      'on-primary': '#fff',
      'primary-darken-1': staticPrimaryDarkenColor,
      'secondary': '#9BA3BE',
      'secondary-darken-1': '#8992AF',
      'on-secondary': '#0B0F19',
      'success': '#22C55E',
      'success-darken-1': '#1DAE53',
      'on-success': '#fff',
      'info': '#4C82F7',
      'info-darken-1': '#3B72E8',
      'on-info': '#fff',
      'warning': '#FFBA08',
      'warning-darken-1': '#E6A700',
      'on-warning': '#0B0F19',
      'error': '#EF4444',
      'error-darken-1': '#DC3A3A',
      'on-error': '#fff',
      'background': '#0F1421',
      'on-background': '#E4E7F1',
      'surface': '#171D2E',
      'on-surface': '#E4E7F1',
      'grey-50': '#151A28',
      'grey-100': '#1B2233',
      'grey-200': '#2A3247',
      'grey-300': '#3A445D',
      'grey-400': '#565F7C',
      'grey-500': '#737C9A',
      'grey-600': '#9BA3BE',
      'grey-700': '#B3BACF',
      'grey-800': '#CDD2E1',
      'grey-900': '#E4E7F1',
      'perfect-scrollbar-thumb': '#2A3247',
      'skin-bordered-background': '#171D2E',
      'skin-bordered-surface': '#171D2E',
      'expansion-panel-text-custom-bg': '#1B2233',
      'track-bg': '#2A3247',
      'chat-bg': '#1B2233',
    },

    variables: {
      'code-color': '#d400ff',
      'overlay-scrim-background': '#0B0F19',
      'tooltip-background': '#E4E7F1',
      'overlay-scrim-opacity': 0.5,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'disabled-opacity': 0.4,
      'dragged-opacity': 0.1,
      'border-color': '#E4E7F1',
      'border-opacity': 0.12,
      'table-header-color': '#1B2233',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,

      // 👉 Shadows
      'shadow-key-umbra-color': '#05070C',
      'shadow-xs-opacity': '0.20',
      'shadow-sm-opacity': '0.22',
      'shadow-md-opacity': '0.24',
      'shadow-lg-opacity': '0.26',
      'shadow-xl-opacity': '0.28',
    },
  },
}

export default themes
